// Graph model — normalize Prism `sankeyData` into a typed node/edge graph plus
// the identity-first view derivations (Users default, Alerts). This is the
// focused Phase-1 stand-in for agate's prismSankeyToGraphData: enough structure
// to render Users + Alerts on live data, without the tree-flattening machinery.
import type {
  SankeyData, SankeyNode, AlertOriginated, BluepythonAgent, ObservedUser,
} from "@/lib/api/prism";

export type Sev = "crit" | "high" | "med" | "low" | null;
export type Presence = "active" | "idle" | "offline";

export interface Finding {
  id: string; sev: Exclude<Sev, null>; title: string; rule: string; alertType: string; count: number;
  /** The alert's human message (e.g. the blocked command), when present. */
  message?: string;
}
export interface GNode {
  id: string;
  kind: "host" | "server" | "agent" | "llm" | "tool" | "user" | "sandbox" | "data" | "runtime";
  label: string;
  sub?: string;
  presence?: Presence;
  /** Canonical session id (agent nodes only) — keys the Prism sessionFullTrace ladder. */
  sessionId?: string;
  findings: Finding[];
  worst: Sev;
  fields: Record<string, string>;
}
export interface GEdge { id: string; source: string; target: string; kind: string; status: string }
export interface Graph {
  nodes: Map<string, GNode>;
  edges: GEdge[];
  userIds: string[]; // observed-user node ids (root of the Users view)
}
export interface ViewLayout { visible: string[]; edges: GEdge[]; focus: string | null }

const SEV_ORDER: Record<string, number> = { crit: 4, high: 3, med: 2, low: 1 };
export const sevRank = (s: Sev) => (s ? SEV_ORDER[s] ?? 0 : 0);

// CCPD severity string → our tier. Unknown-with-alert never downgrades below crit.
function toSev(severity?: string): Exclude<Sev, null> {
  switch ((severity || "").toUpperCase()) {
    case "CRITICAL": return "crit";
    case "ERROR": return "high";
    case "WARNING": return "med";
    case "INFO": return "low";
    default: return "crit";
  }
}
function alertsOf(a: SankeyNode["alert_originated"]): AlertOriginated[] {
  if (!a) return [];
  return Array.isArray(a) ? a : [a];
}
// One canonical label resolver (never surface a raw id when a name exists).
function label(n: SankeyNode): string {
  return n.shortName || n.label || n.metadata?.serverName || n.id;
}
// Agent node ids carry the runtime as a `<runtime>:<session>` prefix
// (e.g. "claude_code:demo-live-1"). We interpose one hub per runtime so the
// Users view reads person → runtime → the agents it ran.
const RUNTIME_LABEL: Record<string, string> = {
  claude_code: "Claude Code", claude_desktop: "Claude Desktop",
  codex: "Codex", cursor: "Cursor", cline: "Cline", windsurf: "Windsurf",
};
function runtimeKey(agentNodeId: string): string {
  const i = agentNodeId.indexOf(":");
  return i > 0 ? agentNodeId.slice(0, i) : "agent";
}
function runtimeLabel(key: string): string {
  return RUNTIME_LABEL[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function kindOf(type: string): GNode["kind"] {
  switch (type) {
    case "server": return "server";
    case "llm": return "llm";
    case "tool": return "tool";
    case "sandbox": return "sandbox";
    case "sandboxed-process": return "sandbox";
    case "instance": case "host": return "host";
    default: return "agent"; // agent | client | a2a_agent
  }
}
function edgeKind(s?: GNode, t?: GNode): string {
  if (!s || !t) return "unknown";
  if (s.kind === "agent" && t.kind === "server") return "client-server";
  if (s.kind === "agent" && t.kind === "agent") return "agent-agent";
  if (s.kind === "agent" && t.kind === "llm") return "agent-llm";
  if (t.kind === "tool" || s.kind === "tool") return "agent-tool";
  return "flow";
}

export function buildGraph(data: SankeyData): Graph {
  const nodes = new Map<string, GNode>();
  const byId = new Map<string, SankeyNode>();

  for (const n of data.nodes ?? []) {
    byId.set(n.id, n);
    const findings: Finding[] = alertsOf(n.alert_originated).map((a) => ({
      id: a.short_name || a.alert_id,
      sev: toSev(a.severity),
      title: a.alert_type ? a.alert_type.replace(/_/g, " ") : "alert",
      rule: a.alert_type ?? "alert",
      alertType: a.alert_type ?? "alert",
      count: a.alertCount ?? 1,
      message: a.message ?? undefined,
    }));
    const worst = findings.reduce<Sev>((w, f) => (sevRank(f.sev) > sevRank(w) ? f.sev : w), null);
    const fields: Record<string, string> = {};
    const m = n.metadata;
    if (m?.hostId) fields["Host"] = m.hostId;
    if (m?.serverName) fields["Server"] = m.serverName;
    if (m?.endpoint) fields["Endpoint"] = m.endpoint;
    if (m?.sandboxRuntime) fields["Runtime"] = m.sandboxRuntime;
    if (m?.processName) fields["Process"] = m.processName;
    fields["Type"] = n.type;
    const lbl = label(n);
    nodes.set(n.id, {
      id: n.id, kind: kindOf(n.type), label: lbl,
      sub: n.shortName && n.shortName !== lbl ? n.shortName : undefined,
      findings, worst, fields,
    });
  }

  // edges from links; derive typed kind + carry status. presence is computed
  // per-agent from the statuses of its incident links.
  const edges: GEdge[] = [];
  const agentStatuses = new Map<string, Set<string>>();
  (data.links ?? []).forEach((l, i) => {
    const s = nodes.get(l.source), t = nodes.get(l.target);
    if (!s || !t) return;
    edges.push({ id: `e${i}-${l.sessionId ?? ""}`, source: l.source, target: l.target, kind: edgeKind(s, t), status: l.status || "unknown" });
    for (const id of [l.source, l.target]) {
      const gn = nodes.get(id);
      if (gn?.kind === "agent") {
        (agentStatuses.get(id) ?? agentStatuses.set(id, new Set()).get(id)!).add(l.status || "");
        // First active link's session wins; else fall back to any session so the
        // trace ladder always has a key. Active sessions are the live thread.
        if (l.sessionId && (!gn.sessionId || l.status === "active")) gn.sessionId = l.sessionId;
      }
    }
  });
  for (const [id, st] of agentStatuses) {
    const n = nodes.get(id); if (!n) continue;
    // historical sessions ⇒ idle (was active, now quiet); only a truly link-less
    // agent is offline. Keeps the presence read honest without a sea of "offline".
    n.presence = st.has("active") ? "active" : (st.has("idle") || st.has("historical")) ? "idle" : "offline";
  }

  // Users: overlay observed-user identity. Each observedUser becomes a node;
  // each bluepythonAgent maps (by short_name) to its owner, adding a user→agent edge.
  const userIds: string[] = [];
  const userById = new Map<string, ObservedUser>();
  for (const u of data.observedUsers ?? []) userById.set(u.user_id, u);
  const agentByShort = new Map<string, string>(); // short_name -> node id
  for (const [id, n] of nodes) if (n.kind === "agent") agentByShort.set(n.label, id);

  function ensureUser(u: ObservedUser): string {
    const uid = `user:${u.user_id}`;
    if (!nodes.has(uid)) {
      nodes.set(uid, {
        id: uid, kind: "user",
        label: u.display_name && u.display_name !== "no-user" ? u.display_name : (u.user_id === "no-user" ? "No user" : u.user_id),
        sub: u.user_id !== "no-user" ? u.user_id : undefined,
        findings: [], worst: null, fields: { User: u.user_id, Identity: u.short_name },
      });
      userIds.push(uid);
    }
    return uid;
  }
  const ownedByUser = new Map<string, string[]>(); // uid -> owned agent node ids
  for (const ba of (data.bluepythonAgents ?? []) as BluepythonAgent[]) {
    const agentId = agentByShort.get(ba.short_name);
    if (!agentId) continue;
    const ou = ba.observed_user;
    const u = ou ? userById.get(ou.user_id) ?? { id: ou.short_name, user_id: ou.user_id, short_name: ou.short_name, display_name: ou.display_name } : null;
    if (!u) continue;
    const uid = ensureUser(u);
    if (ba.hostid) nodes.get(agentId)!.fields["Host"] = ba.hostid;
    nodes.get(agentId)!.fields["User"] = u.user_id;
    (ownedByUser.get(uid) ?? ownedByUser.set(uid, []).get(uid)!).push(agentId);
  }

  // Build identity structure. Identified users get a per-runtime hub interposed
  // (person → "Claude Code" → the agents it ran); the anonymous "No user" bucket
  // stays flat and collapsed (usersView never expands it).
  const hubAgents = new Map<string, Set<string>>(); // hubId -> agent node ids
  for (const [uid, agentIds] of ownedByUser) {
    const u = nodes.get(uid)!;
    u.fields["Agents"] = String(agentIds.length);
    if (u.label === "No user") {
      u.sub = `${agentIds.length} unidentified agent${agentIds.length === 1 ? "" : "s"}`;
      for (const aid of agentIds) edges.push({ id: `u-${uid}-${aid}`, source: uid, target: aid, kind: "owns", status: "identity" });
      continue;
    }
    const byRuntime = new Map<string, string[]>();
    for (const aid of agentIds) {
      const k = runtimeKey(aid);
      (byRuntime.get(k) ?? byRuntime.set(k, []).get(k)!).push(aid);
    }
    for (const [rk, aids] of byRuntime) {
      const hubId = `rt:${uid}:${rk}`;
      const anyActive = aids.some((a) => nodes.get(a)?.presence === "active");
      const anyIdle = aids.some((a) => nodes.get(a)?.presence === "idle");
      nodes.set(hubId, {
        id: hubId, kind: "runtime", label: runtimeLabel(rk),
        sub: `${aids.length} agent${aids.length === 1 ? "" : "s"}`,
        presence: anyActive ? "active" : anyIdle ? "idle" : "offline",
        findings: [], worst: null, fields: { Agents: String(aids.length) },
      });
      hubAgents.set(hubId, new Set(aids));
      edges.push({ id: `u-${uid}-${hubId}`, source: uid, target: hubId, kind: "owns", status: "identity" });
      for (const aid of aids) edges.push({ id: `r-${hubId}-${aid}`, source: hubId, target: aid, kind: "runs", status: "identity" });
    }
  }

  // annotate each hub with the models + MCP servers its agents reached
  for (const [hubId, aids] of hubAgents) {
    const models = new Set<string>(), servers = new Set<string>();
    for (const e of edges) {
      if (!aids.has(e.source)) continue;
      const t = nodes.get(e.target);
      if (t?.kind === "llm") models.add(e.target);
      else if (t?.kind === "server") servers.add(e.target);
    }
    const hub = nodes.get(hubId)!;
    if (models.size) hub.fields["Models"] = String(models.size);
    if (servers.size) hub.fields["MCP servers"] = String(servers.size);
  }

  return { nodes, edges, userIds };
}

function neighborsWithin(graph: Graph, seed: Set<string>, edges: GEdge[]): { visible: Set<string>; edges: GEdge[] } {
  const visible = new Set(seed);
  const kept: GEdge[] = [];
  for (const e of edges) {
    if (seed.has(e.source) || seed.has(e.target)) { visible.add(e.source); visible.add(e.target); kept.push(e); }
  }
  return { visible, edges: kept };
}

// Users view (default, identity-first): users → their agents → resources those
// agents touch. Non-identity edges are the client-server / agent-llm links.
export function usersView(graph: Graph): ViewLayout {
  const isNoUser = (id: string) => graph.nodes.get(id)?.label === "No user";
  const seed = new Set<string>(graph.userIds); // both user roots show
  const kept: GEdge[] = [];
  // person → runtime hub (identified users only). The "No user" bucket stays a
  // single collapsed node so anonymous agents don't swamp the view; they surface
  // as real people once they report identity.
  const ownEdges = graph.edges.filter((e) => e.kind === "owns" && !isNoUser(e.source));
  for (const e of ownEdges) { seed.add(e.target); kept.push(e); }
  // runtime hub → the agents it ran
  const runEdges = graph.edges.filter((e) => e.kind === "runs" && seed.has(e.source));
  for (const e of runEdges) { seed.add(e.target); kept.push(e); }
  // agents → the models / MCP servers / tools they touched
  const agentIds = new Set([...seed].filter((id) => graph.nodes.get(id)?.kind === "agent"));
  for (const e of graph.edges) {
    if (e.kind === "owns" || e.kind === "runs") continue;
    if (agentIds.has(e.source) || agentIds.has(e.target)) { seed.add(e.source); seed.add(e.target); kept.push(e); }
  }
  const focus = graph.userIds.find((id) => !isNoUser(id)) ?? graph.userIds[0] ?? null;
  return { visible: [...seed], edges: kept, focus };
}

// Alerts view: alerting entities + their immediate neighborhood, severity-ranked.
export function alertsView(graph: Graph): ViewLayout {
  const seed = new Set<string>();
  for (const [id, n] of graph.nodes) if (n.findings.length) seed.add(id);
  if (!seed.size) return { visible: [], edges: [], focus: null };
  const { visible, edges } = neighborsWithin(graph, seed, graph.edges.filter((e) => e.kind !== "owns"));
  const focus = [...seed].sort((a, b) => sevRank(graph.nodes.get(b)!.worst) - sevRank(graph.nodes.get(a)!.worst))[0] ?? null;
  return { visible: [...visible], edges, focus };
}
